import React, { FunctionComponent, ReactNode, SyntheticEvent } from 'react'
import clsx from 'clsx'
import { ShellLink } from '../Link'
import { ShellButton, ButtonTypes } from '../Button'
import { getSemanticKind, KINDS } from './constants'
import styles from './style.module.scss'
import { DesignLanguage, useDesignLanguagePerClass, useTheme } from '../Theme'

type CSSClasses = keyof typeof styles

type KindsType = typeof KINDS
type KindsKeys = keyof KindsType
export type TagKinds = KindsType[KindsKeys]

export const TagDesignLanguageVariantMapping: {
  [K in DesignLanguage]?: {
    [T in TagKinds]?: TagKinds
  }
} = {
  [DesignLanguage.Arcade]: {
    Primary: 'Gray',
    Mint: 'Gray',
  },
} as const

export interface TagProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement | HTMLButtonElement | HTMLSpanElement>, 'onClick' | 'style'> {
  children: ReactNode
  onClick?: (e: SyntheticEvent) => void
  type?: ButtonTypes | string
  kind?: TagKinds
  testNameSpace?: string
}

export type TagVariantComponent = FunctionComponent<TagProps>

export type TagComponent = TagVariantComponent & {
  /**
   * @deprecated To be removed in favour of Flat
   */
  White: TagVariantComponent
  /**
   * @deprecated To be removed in favour of Inverse
   */
  Black: TagVariantComponent
  /**
   * @deprecated To be removed in favour of Subtle
   */
  Gray: TagVariantComponent
  /**
   * @deprecated To be removed in favour of Primary
   */
  Mint: TagVariantComponent

  Flat: TagVariantComponent
  Inverse: TagVariantComponent
  Subtle: TagVariantComponent
  Primary: TagVariantComponent
}
export interface BaseTagProps extends React.AnchorHTMLAttributes<HTMLSpanElement> {
  children: ReactNode
  kind?: TagKinds
  testNameSpace?: string
  isButton?: boolean
  isLink?: boolean
}

export type BaseTagComponent = FunctionComponent<BaseTagProps>

const BaseTag: BaseTagComponent = ({ children, kind = KINDS.FLAT, testNameSpace = '', isButton = false, isLink = false, ...otherProps }) => {
  const designLanguageStyles = useDesignLanguagePerClass(styles)
  const resolvedKind = getSemanticKind(kind)
  const TagClassNames = clsx(
    designLanguageStyles.tag,
    isButton && designLanguageStyles.tagButton,
    isLink && designLanguageStyles.tagLink,
    designLanguageStyles[`tag${resolvedKind}` as CSSClasses]
  )

  return (
    <span className={TagClassNames} {...otherProps} {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-tag` })}>
      {children}
    </span>
  )
}

const getButtonType = (type?: string): ButtonTypes | undefined => {
  if (typeof type !== 'undefined' && (type === 'button' || type === 'submit')) {
    return type
  }
}

const getKindOverride = (designLanguage: DesignLanguage, kind: TagKinds | undefined): TagKinds | undefined => {
  if (kind === undefined) {
    return kind
  }
  return TagDesignLanguageVariantMapping[designLanguage]?.[kind] ?? kind
}

export const Tag: TagComponent = ({ href, onClick, kind, children, type, testNameSpace = '', ...otherProps }: TagProps) => {
  const {
    theme: { designLanguage },
  } = useTheme()
  const kindOverride = getKindOverride(designLanguage, kind)
  if (typeof href !== 'undefined' && typeof onClick !== 'undefined') {
    console.warn('You can not pass the onClick and the href at the same time.')
    return null
  }

  if (typeof href !== 'undefined') {
    return (
      <ShellLink href={href} type={type} testNameSpace={testNameSpace} {...otherProps}>
        <BaseTag isLink kind={kindOverride}>
          {children}
        </BaseTag>
      </ShellLink>
    )
  }

  if (typeof onClick !== 'undefined') {
    return (
      <ShellButton onClick={onClick} type={getButtonType(type)} testNameSpace={testNameSpace} {...otherProps}>
        <BaseTag isButton kind={kindOverride}>
          {children}
        </BaseTag>
      </ShellButton>
    )
  }

  return (
    <BaseTag kind={kindOverride} testNameSpace={testNameSpace} {...otherProps}>
      {children}
    </BaseTag>
  )
}

const TagFlat: TagVariantComponent = (props) => {
  const { children, kind, ...otherProps } = props

  return (
    <Tag kind={KINDS.FLAT} {...otherProps}>
      {children}
    </Tag>
  )
}

const TagInverse: TagVariantComponent = (props) => {
  const { children, kind, ...otherProps } = props

  return (
    <Tag kind={KINDS.INVERSE} {...otherProps}>
      {children}
    </Tag>
  )
}

const TagSubtle: TagVariantComponent = (props) => {
  const { children, kind, ...otherProps } = props

  return (
    <Tag kind={KINDS.SUBTLE} {...otherProps}>
      {children}
    </Tag>
  )
}

const TagPrimary: TagVariantComponent = (props) => {
  const { children, kind, ...otherProps } = props

  return (
    <Tag kind={KINDS.PRIMARY} {...otherProps}>
      {children}
    </Tag>
  )
}

/**
 * @deprecated To be removed in favour of Tag.Flat
 */
Tag.White = TagFlat
/**
 * @deprecated To be removed in favour of Tag.Inverse
 */
Tag.Black = TagInverse
/**
 * @deprecated To be removed in favour of Tag.Subtle
 */
Tag.Gray = TagSubtle
/**
 * @deprecated To be removed in favour of Tag.Primary
 */
Tag.Mint = TagPrimary

Tag.Flat = TagFlat
Tag.Inverse = TagInverse
Tag.Subtle = TagSubtle
Tag.Primary = TagPrimary
